import { MetadataOptionsQuestion, MeterConfiguration, NumberQuestion } from '../../../types';
import { QuestionInterface } from '../../../types/oldWebForms';
import { reBuildQuestion } from '../reBuildQuestion';

/**
 * Normalizes a question's meter data into the meterConfigurations array format.
 * Handles backward-compatible migration from legacy meterId/associatedQuestions fields
 * and legacy updateData='1' location mode.
 */
export const normalizeMeterConfigurations = (
  question: MetadataOptionsQuestion,
): MeterConfiguration[] => {
  if (question.meterConfigurations?.length) {
    return question.meterConfigurations;
  }
  if (question.meterId) {
    return [
      {
        meterId: question.meterId,
        associatedQuestions: question.associatedQuestions ?? [],
      },
    ];
  }
  if (question.updateData === '1') {
    return [
      {
        meterId: '',
        associatedQuestions: question.associatedQuestions ?? [],
        isLocation: true,
      },
    ];
  }
  return [];
};

/**
 * Adds a new meter configuration to a question.
 */
export const addEmptyMeterConfiguration = (
  questionId: string,
  tableId: string | undefined,
  questions: QuestionInterface[],
): QuestionInterface[] => {
  return reBuildQuestion(
    (question: MetadataOptionsQuestion) => {
      const updatedQuestion = { ...question };
      const existing = normalizeMeterConfigurations(updatedQuestion);
      updatedQuestion.meterConfigurations = [...existing, { meterId: '', associatedQuestions: [] }];
      delete updatedQuestion.meterId;
      delete updatedQuestion.associatedQuestions;
      return updatedQuestion;
    },
    questionId,
    tableId,
  )(questions);
};

/**
 * Updates the meterId of a specific meter configuration.
 * Clears the associatedQuestions for that config since the meter changed.
 * Returns the question IDs that had associatedMetadata pointing to the old meter config,
 * so callers can clean up associatedMetadata on those number questions.
 *
 * Also handles:
 * - Switching to location mode (newMeterId='nestable_location') sets isLocation: true
 * - Switching from location mode to a meter removes isLocation flag
 */
export const updateMeterConfigurationMeterId = (
  questionId: string,
  configIndex: number,
  newMeterId: string,
  tableId: string | undefined,
  questions: QuestionInterface[],
): { updatedQuestions: QuestionInterface[]; questionIdsToClean: string[] } => {
  const questionIdsToClean: string[] = [];

  const updatedQuestions = reBuildQuestion(
    (question: MetadataOptionsQuestion) => {
      const updatedQuestion = { ...question };
      const configs = normalizeMeterConfigurations(updatedQuestion);

      if (configIndex >= configs.length) return updatedQuestion;

      const oldConfig = configs[configIndex];
      questionIdsToClean.push(...(oldConfig.associatedQuestions ?? []));

      const isLocationConfig = newMeterId === 'nestable_location';
      const newConfigs = configs.map((cfg, i) =>
        i === configIndex
          ? {
              meterId: isLocationConfig ? '' : newMeterId,
              associatedQuestions: [],
              ...(isLocationConfig ? { isLocation: true } : {}),
            }
          : cfg,
      );

      updatedQuestion.meterConfigurations = newConfigs;
      delete updatedQuestion.meterId;
      delete updatedQuestion.associatedQuestions;
      delete updatedQuestion.updateData;
      return updatedQuestion;
    },
    questionId,
    tableId,
  )(questions);

  return { updatedQuestions, questionIdsToClean };
};

/**
 * Removes an entire meter configuration by index.
 * Returns the associated question IDs that need their associatedMetadata cleaned up.
 */
export const deleteMeterConfiguration = (
  questionId: string,
  configIndex: number,
  tableId: string | undefined,
  questions: QuestionInterface[],
): { updatedQuestions: QuestionInterface[]; questionIdsToClean: string[] } => {
  const questionIdsToClean: string[] = [];

  const updatedQuestions = reBuildQuestion(
    (question: MetadataOptionsQuestion) => {
      const updatedQuestion = { ...question };
      const configs = normalizeMeterConfigurations(updatedQuestion);
      if (configIndex >= configs.length) return updatedQuestion;

      questionIdsToClean.push(...(configs[configIndex].associatedQuestions ?? []));
      updatedQuestion.meterConfigurations = configs.filter((_, i) => i !== configIndex);
      delete updatedQuestion.meterId;
      delete updatedQuestion.associatedQuestions;
      return updatedQuestion;
    },
    questionId,
    tableId,
  )(questions);

  return { updatedQuestions, questionIdsToClean };
};

/**
 * Adds an empty slot to the associatedQuestions of a specific meter configuration.
 */
export const addMeterConfigurationQuestion = (
  questionId: string,
  configIndex: number,
  tableId: string | undefined,
  questions: QuestionInterface[],
): QuestionInterface[] => {
  return reBuildQuestion(
    (question: MetadataOptionsQuestion) => {
      const updatedQuestion = { ...question };
      const configs = normalizeMeterConfigurations(updatedQuestion);
      if (configIndex >= configs.length) return updatedQuestion;

      const newConfigs = configs.map((cfg, i) =>
        i === configIndex
          ? { ...cfg, associatedQuestions: [...(cfg.associatedQuestions || []), ''] }
          : cfg,
      );
      updatedQuestion.meterConfigurations = newConfigs;
      delete updatedQuestion.meterId;
      delete updatedQuestion.associatedQuestions;
      return updatedQuestion;
    },
    questionId,
    tableId,
  )(questions);
};

/**
 * Updates a specific associated question slot within a meter configuration.
 * Returns the old question ID (to clean its associatedMetadata) and the new question info
 * (to set associatedMetadata on the newly selected number question).
 */
export const updateMeterConfigurationQuestion = (
  questionId: string,
  configIndex: number,
  questionSlotIndex: number,
  newValue: string,
  tableId: string | undefined,
  questions: QuestionInterface[],
): {
  updatedQuestions: QuestionInterface[];
  questionIdToClean: string | undefined;
  questionInfoToAssociate: { questionId: string; meterId: string } | undefined;
} => {
  let questionIdToClean: string | undefined;
  let questionInfoToAssociate: { questionId: string; meterId: string } | undefined;

  const updatedQuestions = reBuildQuestion(
    (question: MetadataOptionsQuestion) => {
      const updatedQuestion = { ...question };
      const configs = normalizeMeterConfigurations(updatedQuestion);
      if (configIndex >= configs.length) return updatedQuestion;

      const config = configs[configIndex];
      if (questionSlotIndex >= config.associatedQuestions.length) return updatedQuestion;

      questionIdToClean = config.associatedQuestions[questionSlotIndex];
      questionInfoToAssociate = { questionId: newValue, meterId: config.meterId };

      const newConfigs = configs.map((cfg, i) => {
        if (i !== configIndex) return cfg;
        const newAssociated = [...cfg.associatedQuestions];
        newAssociated[questionSlotIndex] = newValue;
        return { ...cfg, associatedQuestions: newAssociated };
      });
      updatedQuestion.meterConfigurations = newConfigs;
      delete updatedQuestion.meterId;
      delete updatedQuestion.associatedQuestions;
      return updatedQuestion;
    },
    questionId,
    tableId,
  )(questions);

  return { updatedQuestions, questionIdToClean, questionInfoToAssociate };
};

/**
 * Removes a specific associated question slot from a meter configuration.
 * Returns the question ID that needs its associatedMetadata cleaned up.
 */
export const deleteMeterConfigurationQuestion = (
  questionId: string,
  configIndex: number,
  questionSlotIndex: number,
  tableId: string | undefined,
  questions: QuestionInterface[],
): { updatedQuestions: QuestionInterface[]; questionIdToClean: string | undefined } => {
  let questionIdToClean: string | undefined;

  const updatedQuestions = reBuildQuestion(
    (question: MetadataOptionsQuestion) => {
      const updatedQuestion = { ...question };
      const configs = normalizeMeterConfigurations(updatedQuestion);
      if (configIndex >= configs.length) return updatedQuestion;

      const config = configs[configIndex];
      questionIdToClean = config.associatedQuestions[questionSlotIndex];

      const newConfigs = configs.map((cfg, i) => {
        if (i !== configIndex) return cfg;
        return {
          ...cfg,
          associatedQuestions: cfg.associatedQuestions.filter((_, qi) => qi !== questionSlotIndex),
        };
      });
      updatedQuestion.meterConfigurations = newConfigs;
      delete updatedQuestion.meterId;
      delete updatedQuestion.associatedQuestions;
      return updatedQuestion;
    },
    questionId,
    tableId,
  )(questions);

  return { updatedQuestions, questionIdToClean };
};

/**
 * Clears all meter configurations from a question (used when toggling off the meter feature).
 */
export const clearAllMeterConfigurations = (
  questionId: string,
  tableId: string | undefined,
  questions: QuestionInterface[],
): { updatedQuestions: QuestionInterface[]; questionIdsToClean: string[] } => {
  const questionIdsToClean: string[] = [];

  const updatedQuestions = reBuildQuestion(
    (question: MetadataOptionsQuestion) => {
      const updatedQuestion = { ...question };
      const configs = normalizeMeterConfigurations(updatedQuestion);
      configs.forEach((cfg) => questionIdsToClean.push(...cfg.associatedQuestions));

      delete updatedQuestion.meterConfigurations;
      delete updatedQuestion.meterId;
      delete updatedQuestion.updateData;
      delete updatedQuestion.associatedQuestions;
      return updatedQuestion;
    },
    questionId,
    tableId,
  )(questions);

  return { updatedQuestions, questionIdsToClean };
};

/**
 * Adds associatedMetadata to a question that was linked to a meter configuration.
 * For number questions, sets { meterId, questionId }.
 * For location configs (non-number questions), sets { updateLocation: '1', questionId }.
 */
export const addAssociatedMetadataForConfig = (
  questionInfoToAssociate: { questionId: string; meterId: string },
  meterQuestionId: string,
  questions: QuestionInterface[],
  tableId: string | undefined,
): QuestionInterface[] => {
  const { questionId, meterId } = questionInfoToAssociate;
  return reBuildQuestion(
    (question: NumberQuestion | MetadataOptionsQuestion) => {
      question.associatedMetadata = {
        ...(question.type === 'number' ? { meterId } : { updateLocation: '1' }),
        questionId: meterQuestionId,
      };
      return question;
    },
    questionId,
    tableId,
  )(questions);
};

/**
 * Removes associatedMetadata from a list of question IDs.
 */
export const cleanAssociatedMetadata = (
  questionIds: string[],
  tableId: string | undefined,
  questions: QuestionInterface[],
): QuestionInterface[] => {
  return questionIds.filter(Boolean).reduce((acc, qId) => {
    return reBuildQuestion(
      (question: NumberQuestion | MetadataOptionsQuestion) => {
        delete question.associatedMetadata;
        return question;
      },
      qId,
      tableId,
    )(acc);
  }, questions);
};
